import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, isValid } from 'date-fns';
import {
  Calendar, ChevronLeft, ChevronRight, RefreshCw, Package,
  Truck, Clock, ScanSearch, Cog, Sparkles, CheckCircle,
  AlertTriangle, Phone, User, ArrowRight, X, Check,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:          { label: 'Pending',           color: 'bg-amber-100 text-amber-700 border-amber-200',    icon: Clock },
  pickup_assigned:  { label: 'Pickup Assigned',   color: 'bg-blue-100 text-blue-700 border-blue-200',       icon: UserCheck },
  pickup_done:      { label: 'Picked Up',         color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Package },
  in_process:       { label: 'In Process',        color: 'bg-violet-100 text-violet-700 border-violet-200', icon: Cog },
  process_complete: { label: 'Inspection Done',   color: 'bg-purple-100 text-purple-700 border-purple-200', icon: ScanSearch },
  cleaned:          { label: 'Cleaned',           color: 'bg-teal-100 text-teal-700 border-teal-200',       icon: Sparkles },
  out_for_delivery: { label: 'Out for Delivery',  color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Truck },
  delivered:        { label: 'Delivered',         color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  delivery_overdue: { label: 'Overdue',           color: 'bg-red-100 text-red-700 border-red-200',          icon: AlertTriangle },
};

const PICKUP_TIME_SLOTS = [
  '09:00 – 10:00','10:00 – 11:00','11:00 – 12:00','12:00 – 13:00',
  '13:00 – 14:00','14:00 – 15:00','15:00 – 16:00','16:00 – 17:00',
  '17:00 – 18:00','18:00 – 19:00','19:00 – 20:00',
];

const safeFormat = (val, fmt) => {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? parseISO(val) : val;
    return isValid(d) ? format(d, fmt) : '—';
  } catch { return '—'; }
};

// ── Assign Modal ──────────────────────────────────────────────────────────────
const AssignModal = ({ order, type, boys, onClose, onAssigned }) => {
  const token = localStorage.getItem('token');
  const [boyId, setBoyId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(
    type === 'pickup' ? (order.pickup_date?.slice(0,10) || '') : (order.expected_delivery_date?.slice(0,10) || '')
  );
  const [timeSlot, setTimeSlot] = useState(order.pickup_time_slot || '');
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    if (!boyId) { toast.error('Select a delivery boy'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/delivery-assignments`, {
        order_id: order.id,
        order_type: 'individual',
        assignment_type: type,
        delivery_boy_id: boyId,
        scheduled_date: scheduledDate,
        scheduled_time: timeSlot,
        notes: ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${type === 'pickup' ? 'Pickup' : 'Delivery'} assigned!`);
      onAssigned();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Assign {type === 'pickup' ? 'Pickup' : 'Delivery'} Boy
            </h3>
            <p className="text-sm text-slate-500">{order.customer_name} · {order.customer_phone}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Boy selector */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Select Staff</label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {boys.length === 0 && <p className="text-sm text-slate-400">No delivery boys found</p>}
            {boys.map(b => (
              <button key={b.id} onClick={() => setBoyId(b.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                  boyId === b.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-blue-300'
                }`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0 ${boyId === b.id ? 'bg-white/20' : 'bg-slate-200'}`}>
                  {b.name?.charAt(0).toUpperCase()}
                </div>
                <span className="flex-1 text-left">{b.name}</span>
                {boyId === b.id && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
            {type === 'pickup' ? 'Pickup' : 'Delivery'} Date
          </label>
          <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
        </div>

        {/* Time slot (pickup only) */}
        {type === 'pickup' && (
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Time Slot</label>
            <div className="grid grid-cols-2 gap-1.5 max-h-36 overflow-y-auto">
              {PICKUP_TIME_SLOTS.map(slot => (
                <button key={slot} onClick={() => setTimeSlot(slot)}
                  className={`px-2 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                    timeSlot === slot ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300'
                  }`}>
                  {slot}
                </button>
              ))}
            </div>
          </div>
        )}

        <button onClick={handleAssign} disabled={saving || !boyId}
          className="w-full py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-sm transition-colors">
          {saving ? 'Assigning...' : `Confirm ${type === 'pickup' ? 'Pickup' : 'Delivery'} Assignment`}
        </button>
      </div>
    </div>
  );
};

// ── Order Card ────────────────────────────────────────────────────────────────
// Status groupings for delivery tab logic
const DELIVERY_NOT_READY = new Set(['pending', 'pickup_assigned', 'pickup_done', 'in_process']);
const DELIVERY_READY     = new Set(['process_complete', 'cleaned']);
const DELIVERY_ASSIGNED  = new Set(['out_for_delivery']);
const DELIVERY_OVERDUE   = new Set(['delivery_overdue']);

const OrderCard = ({ order, type, boys, onAssigned }) => {
  const [showAssign, setShowAssign] = useState(false);
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const isOverdue = order.status === 'delivery_overdue';

  const pickupAssigned  = order.assignments?.pickup;
  const deliveryAssigned = order.assignments?.delivery;

  // ── Delivery tab action rendering ──────────────────────────────────────────
  const renderDeliveryAction = () => {
    // Still in pickup/processing stages — not yet ready for delivery
    if (DELIVERY_NOT_READY.has(order.status)) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-50 text-slate-400 border border-slate-200">
          <Clock className="w-3.5 h-3.5" />
          Not ready for delivery yet
        </div>
      );
    }
    // Actively out for delivery
    if (DELIVERY_ASSIGNED.has(order.status)) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-100">
          <Truck className="w-3.5 h-3.5" />
          Out with <span className="font-black ml-1">{deliveryAssigned?.delivery_boy_name || 'delivery boy'}</span>
          {deliveryAssigned?.scheduled_date && <span className="text-slate-400 ml-1">· {safeFormat(deliveryAssigned.scheduled_date, 'dd MMM')}</span>}
        </div>
      );
    }
    // Overdue — needs re-assignment
    if (DELIVERY_OVERDUE.has(order.status)) {
      return (
        <button onClick={() => setShowAssign(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border bg-red-600 text-white border-red-600 hover:bg-red-700 transition-all">
          <AlertTriangle className="w-3 h-3" />
          Re-assign Delivery Boy
        </button>
      );
    }
    // Ready for delivery (cleaned / process_complete) — assign if not yet assigned
    if (DELIVERY_READY.has(order.status)) {
      if (deliveryAssigned) {
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
            <UserCheck className="w-3.5 h-3.5" />
            Assigned to <span className="font-black ml-1">{deliveryAssigned.delivery_boy_name}</span>
            {deliveryAssigned.scheduled_date && <span className="text-slate-400 ml-1">· {safeFormat(deliveryAssigned.scheduled_date, 'dd MMM')}</span>}
          </div>
        );
      }
      return (
        <button onClick={() => setShowAssign(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border bg-teal-600 text-white border-teal-600 hover:bg-teal-700 transition-all">
          <ArrowRight className="w-3 h-3" />
          Assign Delivery Boy
        </button>
      );
    }
    return null;
  };

  // ── Pickup tab action rendering ─────────────────────────────────────────────
  const renderPickupAction = () => {
    if (pickupAssigned) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
          <UserCheck className="w-3.5 h-3.5" />
          Assigned to <span className="font-black ml-1">{pickupAssigned.delivery_boy_name}</span>
          {pickupAssigned.scheduled_date && <span className="text-slate-400 ml-1">· {safeFormat(pickupAssigned.scheduled_date, 'dd MMM')}</span>}
        </div>
      );
    }
    return (
      <button onClick={() => setShowAssign(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border bg-blue-600 text-white border-blue-600 hover:bg-blue-700 transition-all">
        <ArrowRight className="w-3 h-3" />
        Assign Pickup Boy
      </button>
    );
  };

  return (
    <>
      <div className={`bg-white border rounded-2xl px-4 py-4 shadow-sm ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm shadow-blue-100">
            {order.customer_name?.charAt(0)?.toUpperCase() || '?'}
          </div>

          <div className="flex-1 min-w-0">
            {/* Name + status */}
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <p className="font-bold text-slate-800 text-sm">{order.customer_name}</p>
              <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                <Icon className="w-2.5 h-2.5" />{cfg.label}
              </span>
            </div>

            {/* Phone + clothes */}
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mb-2">
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Phone className="w-3 h-3" />{order.customer_phone || '—'}
              </span>
              {order.total_clothes && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Package className="w-3 h-3" />{order.total_clothes} pcs
                </span>
              )}
              {order.total_amount > 0 && (
                <span className={`text-xs font-bold ${order.payment_status === 'paid' ? 'text-emerald-600' : 'text-red-500'}`}>
                  ₹{order.total_amount?.toFixed(0)} · {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
              )}
            </div>

            {/* Dates */}
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mb-2">
              {order.pickup_date && (
                <span className="text-[10px] text-slate-400">
                  Pickup: <span className="font-semibold text-slate-600">
                    {safeFormat(order.pickup_date, 'dd MMM')}{order.pickup_time_slot ? ` · ${order.pickup_time_slot}` : ''}
                  </span>
                </span>
              )}
              {order.expected_delivery_date && (
                <span className="text-[10px] text-slate-400">
                  Due: <span className={`font-semibold ${isOverdue ? 'text-red-600' : 'text-orange-600'}`}>
                    {safeFormat(order.expected_delivery_date, 'dd MMM, h:mm a')}
                  </span>
                </span>
              )}
            </div>

            {/* Address */}
            {order.address_line1 && (
              <p className="text-[10px] text-slate-400 truncate mb-2">{order.address_line1}{order.address_line2 ? `, ${order.address_line2}` : ''}</p>
            )}

            {/* Action */}
            {type === 'pickup' ? renderPickupAction() : renderDeliveryAction()}
          </div>
        </div>
      </div>

      {showAssign && (
        <AssignModal
          order={order}
          type={type}
          boys={boys}
          onClose={() => setShowAssign(false)}
          onAssigned={() => { setShowAssign(false); onAssigned(); }}
        />
      )}
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminDailyOrders = () => {
  const token = localStorage.getItem('token');
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const [activeTab, setActiveTab] = useState('pickups');
  const [date, setDate] = useState(todayStr);
  const [pickupOrders, setPickupOrders] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [boys, setBoys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchBoys = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/delivery-boys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoys(res.data || []);
    } catch {}
  }, [token]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const [pickupRes, deliveryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/daily-order-view`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { date, filter_by: 'pickup_date' }
        }),
        axios.get(`${BACKEND_URL}/api/admin/daily-order-view`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { date, filter_by: 'expected_delivery_date' }
        }),
      ]);
      setPickupOrders(pickupRes.data.orders || []);
      setDeliveryOrders(deliveryRes.data.orders || []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [date, token]);

  useEffect(() => { fetchBoys(); }, [fetchBoys]);
  useEffect(() => { fetchOrders(); setStatusFilter('all'); }, [fetchOrders]);

  const shiftDate = (days) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setDate(format(d, 'yyyy-MM-dd'));
  };

  // Pickup tab: only show orders where pickup hasn't happened yet
  const PICKUP_PENDING_STATUSES = ['pending', 'pickup_assigned'];
  // Delivery tab: hide already-delivered orders
  const orders = activeTab === 'pickups'
    ? pickupOrders.filter(o => PICKUP_PENDING_STATUSES.includes(o.status))
    : deliveryOrders.filter(o => o.status !== 'delivered');

  // Status counts
  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter);

  const tabs = [
    { key: 'pickups',   label: 'Pickups',   icon: Package, count: pickupOrders.length,   color: 'text-blue-600' },
    { key: 'deliveries',label: 'Deliveries',icon: Truck,   count: deliveryOrders.length,  color: 'text-teal-600' },
  ];

  return (
    <div className="space-y-4 pb-24 md:pb-4" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-violet-700 rounded-[2rem] px-6 py-5 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>Daily Dispatch</h1>
            <p className="text-blue-100 text-sm">Assign pickup & delivery boys by date</p>
          </div>
        </div>
      </div>

      {/* Date picker */}
      <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-4 shadow-sm">
        <div className="flex items-center gap-2">
          <button onClick={() => shiftDate(-1)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex-shrink-0">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <button onClick={() => shiftDate(1)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex-shrink-0">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setDate(todayStr)} className="px-3 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-xs font-bold border border-blue-100 hover:bg-blue-100 flex-shrink-0">
            Today
          </button>
          <button onClick={fetchOrders} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex-shrink-0">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-sm font-bold text-slate-700 mt-2.5">
          {format(new Date(date + 'T12:00:00'), 'EEEE, dd MMMM yyyy')}
        </p>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 gap-3">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setStatusFilter('all'); }}
              className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border font-semibold transition-all ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-100'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-200'
              }`}>
              <Icon className={`w-5 h-5 flex-shrink-0 ${activeTab === tab.key ? 'text-white' : tab.color}`} />
              <div className="text-left">
                <p className="text-sm font-bold">{tab.label}</p>
                <p className={`text-xs ${activeTab === tab.key ? 'text-blue-100' : 'text-slate-400'}`}>
                  {tab.count} order{tab.count !== 1 ? 's' : ''}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Status filter pills */}
      {orders.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
              statusFilter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
            }`}>
            All <span className="ml-1 opacity-70">{orders.length}</span>
          </button>
          {Object.entries(statusCounts).sort().map(([status, count]) => {
            const cfg = STATUS_CONFIG[status];
            if (!cfg) return null;
            const Icon = cfg.icon;
            return (
              <button key={status} onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                  statusFilter === status ? cfg.color + ' scale-105 shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                }`}>
                <Icon className="w-3 h-3" />
                {cfg.label}
                <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Orders */}
      {loading && (
        <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-12 text-center shadow-sm">
          <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-12 text-center shadow-sm">
          <Package className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-semibold">
            No {activeTab === 'pickups' ? 'pickups' : 'deliveries'} on this date
          </p>
          <p className="text-slate-400 text-sm mt-1">
            {activeTab === 'pickups'
              ? 'No orders have pickup scheduled on this date'
              : 'No orders have delivery due on this date'}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest px-1">
            {filtered.length} {activeTab === 'pickups' ? 'pickup' : 'delivery'}{filtered.length !== 1 ? 's' : ''}
            {statusFilter !== 'all' ? ` · ${STATUS_CONFIG[statusFilter]?.label}` : ''}
          </p>
          {filtered.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              type={activeTab === 'pickups' ? 'pickup' : 'delivery'}
              boys={boys}
              onAssigned={fetchOrders}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDailyOrders;
